import CodeBlock from '../CodeBlock';

export default function DocsEnvModule() {
  return (
    <section data-docs-section="env" className="text-left mb-16">
      <h2 className="text-2xl font-bold text-white mb-2 group">
        <a href="#env" className="hover:text-sky-400 transition-colors">pa.env</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h2>
      <p className="text-gray-400 mb-6">
        Environment detection — runtime, GPU, and ML framework information as pure data.
        Works on-platform and off. Degrades gracefully when no GPU is present.
      </p>

      <div className="overflow-x-auto mb-6">
        <table className="text-sm text-left w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="py-2 pr-4 text-gray-400 font-medium">Function</th>
              <th className="py-2 pr-4 text-gray-400 font-medium">Returns</th>
              <th className="py-2 text-gray-400 font-medium">Description</th>
            </tr>
          </thead>
          <tbody className="text-gray-300">
            <tr className="border-b border-gray-800/50">
              <td className="py-2 pr-4 font-mono text-sky-400">in_propalgos()</td>
              <td className="py-2 pr-4 text-gray-500">bool</td>
              <td className="py-2 text-gray-400">True when running inside a PropAlgos-managed instance.</td>
            </tr>
            <tr className="border-b border-gray-800/50">
              <td className="py-2 pr-4 font-mono text-sky-400">summary()</td>
              <td className="py-2 pr-4 text-gray-500">None</td>
              <td className="py-2 text-gray-400">Print formatted summary of runtime, Python, GPU, and frameworks.</td>
            </tr>
            <tr className="border-b border-gray-800/50">
              <td className="py-2 pr-4 font-mono text-sky-400">detect_runtime()</td>
              <td className="py-2 pr-4 text-gray-500">RuntimeInfo</td>
              <td className="py-2 text-gray-400">Detect cloud provider, region, instance type from env vars.</td>
            </tr>
            <tr className="border-b border-gray-800/50">
              <td className="py-2 pr-4 font-mono text-sky-400">detect_gpu()</td>
              <td className="py-2 pr-4 text-gray-500">GPUInfo</td>
              <td className="py-2 text-gray-400">Three-tier detection: nvidia-smi → torch.cuda → tensorflow.</td>
            </tr>
            <tr>
              <td className="py-2 pr-4 font-mono text-sky-400">detect_frameworks()</td>
              <td className="py-2 pr-4 text-gray-500">list[FrameworkInfo]</td>
              <td className="py-2 text-gray-400">Scan for PyTorch, TensorFlow, JAX, RAPIDS cuDF/cuML.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <CodeBlock
        code={`import propalgos as pa

# Quick check
if pa.env.in_propalgos():
    print("Running on PropAlgos cloud")

# Full summary
pa.env.summary()
# PropAlgos Environment
# ────────────────────────────────────────
#   Runtime:      PropAlgos Cloud (AWS, us-east-1, g5.xlarge)
#   Python:       3.12.4
#   GPU:          NVIDIA A10G (24 GB) — CUDA 12.8, driver 570.86
#   Frameworks:   PyTorch 2.5.1 (GPU ✓), RAPIDS cuDF 25.2 (GPU ✓)`}
      />

      <h3 className="text-lg font-semibold text-white mt-8 mb-3">Dataclasses</h3>

      <div className="space-y-4 mb-4">
        <div className="p-4 bg-gray-900/50 border border-gray-800 rounded-lg">
          <h4 className="text-sm font-semibold text-white mb-2">RuntimeInfo</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <code className="text-sky-400">on_propalgos</code><span className="text-gray-500">bool</span>
            <code className="text-sky-400">cloud_provider</code><span className="text-gray-500">str | None — "aws", "gcp", "azure"</span>
            <code className="text-sky-400">region</code><span className="text-gray-500">str | None</span>
            <code className="text-sky-400">instance_type</code><span className="text-gray-500">str | None</span>
            <code className="text-sky-400">instance_id</code><span className="text-gray-500">str | None</span>
            <code className="text-sky-400">python_version</code><span className="text-gray-500">str</span>
            <code className="text-sky-400">platform</code><span className="text-gray-500">str</span>
          </div>
        </div>

        <div className="p-4 bg-gray-900/50 border border-gray-800 rounded-lg">
          <h4 className="text-sm font-semibold text-white mb-2">GPUInfo</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <code className="text-sky-400">available</code><span className="text-gray-500">bool</span>
            <code className="text-sky-400">name</code><span className="text-gray-500">str | None — e.g. "NVIDIA A10G"</span>
            <code className="text-sky-400">memory_mb</code><span className="text-gray-500">int | None</span>
            <code className="text-sky-400">driver_version</code><span className="text-gray-500">str | None</span>
            <code className="text-sky-400">cuda_version</code><span className="text-gray-500">str | None</span>
            <code className="text-sky-400">count</code><span className="text-gray-500">int</span>
          </div>
        </div>

        <div className="p-4 bg-gray-900/50 border border-gray-800 rounded-lg">
          <h4 className="text-sm font-semibold text-white mb-2">FrameworkInfo</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <code className="text-sky-400">name</code><span className="text-gray-500">str — "PyTorch", "TensorFlow", etc.</span>
            <code className="text-sky-400">version</code><span className="text-gray-500">str</span>
            <code className="text-sky-400">gpu_enabled</code><span className="text-gray-500">bool</span>
          </div>
        </div>
      </div>
    </section>
  );
}
